<template>
  <section
      ref="heroRef"
      class="content-hero"
      :style="dynamicStyle"
  >
    <h1
        class="content-hero__title"
        :class="{'content-hero__title--image': image, 'content-hero__title--no-image': !image}"
        v-html="title"
    />

    <figure
        v-if="image?.image?.id"
        class="content-hero__figure"
    >
      <AppImageFocal :data="image" :renditions="renditions" :eager="true" class="content-hero__image"/>
    </figure>
  </section>
</template>

<script setup lang="ts">

import {computed, onMounted, reactive, ref, watch} from 'vue'
import {useWindowScroll} from '~/composables/useWindowScroll'

const heroRef = ref<HTMLElement>()

const props = defineProps<{
  image: unknown,
  title: String
}>()

const state = reactive({
  scroll: 0
})

const renditions = [
  {width: 575, height: 323, breakpoint: 'xs'},
  {width: 991, height: 557, breakpoint: 'sm'},
]

const dynamicStyle = computed(() => {
  const height = heroRef.value?.getBoundingClientRect().height | 0
  const opacity = (state.scroll) ? (height - state.scroll) / height : 1
  return {opacity: (opacity < 0) ? 0 : (opacity > 1) ? 1 : opacity}
})

onMounted(() => {
  // watch for scroll
  const scrollState = useWindowScroll()
  watch(scrollState, (newState) => {
    state.scroll = newState.scroll
  })
})

</script>

<style scoped lang="postcss">
.content-hero {
  @mixin container-xl;
  position: sticky;
  top: 0px;
  z-index: -10;
  opacity: 0;
  padding-top: calc(16px + var(--navbar-padding));

  @media (--lg) {
    column-gap: 1.5rem;
  }

  @media (--xl) {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    column-gap: 1.75rem;
    padding-left: 1.75rem;
    padding-right: 1.75rem;
  }

  @media (--xxl) {
    column-gap: 2rem;
    padding-left: 2rem;
    padding-right: 2rem;
  }

  &__title {
    grid-column: 1 / -1;
    hyphens: auto;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    text-align: center;
    font-family: Midnight, sans-serif;
    font-size: 28px;
    text-transform: uppercase;
    line-height: 0.9;
    @mixin hyphenation-specs;

    &--image {
      margin-bottom: 2rem;
    }

    &--no-image {
      margin-bottom: 1.75rem;
    }

    @media (--sm) {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    @media (--md) {
      padding-left: 1.25rem;
      padding-right: 1.25rem;
      font-size: 56px;
    }

    @media (--lg) {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }

    @media (--xl) {
      padding-left: 0px;
      padding-right: 0px;
      line-height: 0.8;

      &--image {
        margin-bottom: 1rem;
      }

      &--no-image {
        margin-bottom: 1rem;
      }
    }
  }

  &__figure {
    grid-column: 1 / -1;
    @mixin app-block-margins;

    @media (--xl) {
      grid-column: span 8 / span 8;
      grid-column-start: 3;
    }
  }

  &__image {
    @media (--xl) {
      margin-left: -1.75rem;
      margin-right: -1.75rem;
    }

    @media (--xxl) {
      margin-left: -2rem;
      margin-right: -2rem;
    }
  }
}
</style>
